/**
 * Evaluates provided route guards and handles redirecting if redirect is provided.
 * Manages error handling and fallback scenarios for route guarding.
 * `routeGuards` - Function or array of functions that can be asynchronous.
 * Once a route guard fails, evaluation stops and the provided redirect is evaluated.
 * `redirectPath` - Optional parameter that can be a string
 * or a function that returns a string. If provided, this string will be used for redirecting.
 * If a redirect value is not provided, route guard will simply prevent routing.
 */
export default function(routeGuards, redirectPath) {
  return defineNuxtRouteMiddleware(async(to, from) => {
    const nuxtApp = useNuxtApp()

    if (!Array.isArray(routeGuards)) {
      routeGuards = [routeGuards]
    }

    for (const guard of routeGuards) {
      let guardPassed

      try {
        guardPassed = await guard(nuxtApp, to, from)
      } catch (error) {
        nuxtApp.$bugsnag.notify(error)
        return navigateTo('/')
      }

      if (guardPassed) {
        continue
      }

      if (typeof redirectPath === 'function') {
        redirectPath = await redirectPath(nuxtApp)
      }

      if (redirectPath) {
        const resolvedRedirect = nuxtApp.$router.resolve(redirectPath)

        // If the redirect does not match any of the router's routes we are navigating outside of the Nuxt application.
        const external = !resolvedRedirect.matched.length

        return navigateTo(redirectPath, { external })
      }

      if (to.path === from.path) {
        // This occurs when a user enters the application on a route that is guarded from them.
        // If we have no `redirectPath` to redirect the user to, we will send them to the policy search screen.
        return navigateTo('/')
      }

      return abortNavigation()
    }
  })
}
